import _ from "lodash";
import React, { useState, useCallback } from "react";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import PropTypes from "prop-types";
import { compose, graphql } from "lib/apolloClient";
import { connect } from "react-redux";
import TagPractices from "./TagPractices";
import EvaluationCycles from "./EvaluationCycles";
import { getSnPEvaluationCyclesQuery } from "SnP/modules/SnPQueries";
import { getSnPEvaluationCyclesFromCache } from "SnP/modules/SnPGraphqlHelpers";
import { getSnpV2Cycles } from "SnP/modules/SnPUtils";
import { tagPractices } from "SnP/modules/SnPModule";
import classes from "./TagSnp.scss";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import {
  SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION,
  SNPv2_VISITOR_PORTAL_PERM_GROUP,
} from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";

import {
  getCycleIdToSetIdMappingMemoized,
  getPracticesForCycleMemoized,
  getAddedPracticesMemoized,
  getRemovedPracticesMemoized,
  getCycleIdToSelectedPracticesCountMappingMemoized,
  getApprovedPracticesForCycle,
  getPracticeWrappersForCycle,
} from "./TagSnpUtils";
import { getUserInfo } from "Login/modules/LoginModule";

/**
 * @params
 *  cycles - list of evaluation cycles corresponding to current curriculum program
 *  tagPracticeMutation - mutation handler for tagging practice with evidence(item)
 *  itemId - id of evidence
 *  closeTagSnP - To close Parent Container of TagSnP
 *  updateTaggedPractices - Function for updating Tagged Practice State outside TagSnP
 *  taggedPractices - data structure having info of previously selected practices
 */

/**data structure info: */
/**taggedPractices -
 * [{evaluationCycle-Object,
 *   taggedPractice:[{
 *                      taggedBy:{id},
 *                      practice:{id},
 *                      evidenceStatus:String}]
 * }] */

const TagSnp = props => {
  const {
    cycles,
    t,
    tagPractices,
    itemId,
    itemType,
    itemGroupType,
    itemTitle,
    closeModal,
    taggedPractices,
    steps,
    parentCycleId,
    parentSetId,
    userId,
    showApprovedPractices,
    assignmentFeedFilters,
    canManageEvaluationCycles,
    isVisitor,
    currentStepIndex,
    setCurrentStepIndex,
    testId,
    reportCourseFilters,
  } = props;

  const [currentCycle, setCurrentCycle] = useState(parentCycleId);
  const [isLoading, setIsLoading] = useState(false);

  const cycleIdToSetIdMapping = getCycleIdToSetIdMappingMemoized({
    cycles,
  });

  const goToNextPage = () => {
    setCurrentStepIndex(currentStepIndex => currentStepIndex + 1);
  };

  const goToPreviousPage = () => {
    setCurrentStepIndex(currentStepIndex => currentStepIndex - 1);
  };

  const updateCurrentCycle = useCallback(({ id }) => {
    setCurrentCycle(id);
    goToNextPage();
  }, []);

  const practiceWrappers = getPracticeWrappersForCycle({
    taggedPractices,
    cycleId: currentCycle,
  });

  const {
    taggedPracticesForCycle,
    disabledPracticesForCycle,
  } = getPracticesForCycleMemoized({
    practiceWrappers,
    userId,
    canManageEvaluationCycles,
  });

  const approvedPractices = showApprovedPractices
    ? getApprovedPracticesForCycle({
        practiceWrappers,
      })
    : [];

  const cycleIdToTaggedPracticesCountMapping = getCycleIdToSelectedPracticesCountMappingMemoized(
    { cycles, taggedPractices }
  );

  const updateTaggedPractices = async ({ selectedPractices }) => {
    setIsLoading(true);

    try {
      await tagPractices({
        evaluationCycleId: currentCycle,
        addedPractices: getAddedPracticesMemoized({
          selectedPractices,
          taggedPractices: taggedPracticesForCycle,
        }),

        removedPractices: getRemovedPracticesMemoized({
          selectedPractices,
          taggedPractices: taggedPracticesForCycle,
        }),
        itemId,
        itemType: itemGroupType ? itemGroupType : itemType,
        assignmentFeedFilters,
        taggedPracticesCount: _.size(selectedPractices),
        reportCourseFilters,
        canManageEvaluationCycles,
        isVisitor,
      });
      setIsLoading(false);
      closeModal();
    } catch (e) {
      setIsLoading(false);
    }
  };

  const handleTaggedPractices = data => {
    if (props.updateTaggedPractices) {
      const selectedPractices = _.get(data, "selectedPractices", []);

      const addedPractices = getAddedPracticesMemoized({
        selectedPractices,
        taggedPractices: taggedPracticesForCycle,
      });

      const removedPractices = getRemovedPracticesMemoized({
        selectedPractices,
        taggedPractices: taggedPracticesForCycle,
      });

      props.updateTaggedPractices({
        evaluationCycle: { id: currentCycle },
        addedPractices,
        removedPractices,
      });
      closeModal();
    } else updateTaggedPractices(data);
  };

  const closeButtonElement = (
    <IconButton
      onClick={closeModal}
      testId={`${testId}-tagSnp-close`}
      icon={<CloseOutlined variant="subtle" />}
      variant={"neutral"}
      type="plain"
      size={"medium"}
      dsVersion="2.0"
      aria-label={t("common:close")}
    />
  );

  const currentCycleDetails = _.find(cycles, ["id", currentCycle]) ?? "";

  const { title: cycleName = "", cycleType = "" } = currentCycleDetails;

  const practiceListsCollection = {
    approvedPractices,
    taggedPractices: taggedPracticesForCycle,
    disabledPractices: disabledPracticesForCycle,
  };

  const getCurrentScreen = () => {
    const currentStep = steps[currentStepIndex];
    switch (currentStep) {
      case "CYCLE_LIST": {
        return (
          <EvaluationCycles
            updateCurrentCycle={updateCurrentCycle}
            cycles={cycles}
            closeButtonElement={closeButtonElement}
            cycleIdToTaggedPracticesCountMapping={
              cycleIdToTaggedPracticesCountMapping
            }
            t={t}
            testId={`${testId}-tagSnp`}
          />
        );
      }
      case "STANDARD_LIST": {
        return (
          <TagPractices
            itemTitle={itemTitle}
            setId={cycleIdToSetIdMapping[currentCycle] ?? parentSetId}
            goToPreviousPage={goToPreviousPage}
            cycleDetails={{ cycleName, cycleType }}
            closeButtonElement={closeButtonElement}
            handleTaggedPractices={handleTaggedPractices}
            practiceListsCollection={practiceListsCollection}
            showBackButton={_.size(cycles) > 1 && currentStepIndex > 0}
            t={t}
            testId={`${testId}-tagSnp`}
            itemType={itemType}
          />
        );
      }
    }
  };

  return (
    <div className={classes.container}>
      {getCurrentScreen()}

      {isLoading ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const organizationId = userInfo.org_id;
  const curriculumProgramId = ownProps.curriculumProgramId;
  const userId = userInfo.id;

  const variables = {
    id: organizationId,
    filters: {
      isArchived: false,
      curriculumProgramId,
    },
  };

  const canManageEvaluationCycles = ACLStorage.getPermissionValue(
    SNPv2_MANAGE_EVALUATION_CYCLES_PERMISSION
  );

  const isVisitor = ACLStore.can(SNPv2_VISITOR_PORTAL_PERM_GROUP);

  return {
    userId,
    variables,
    isData: true,
    isLoading: false,
    canManageEvaluationCycles,
    isVisitor,
  };
};

const mapActionCreators = {
  tagPractices,
};

export default compose(
  I18nHOC({ resources: ["snpTemplate", "snp", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getSnPEvaluationCyclesQuery, {
    name: "getSnPEvaluationCyclesQuery",
    alias: "getSnPEvaluationCyclesQuery",
    options: ({ variables }) => {
      return {
        variables,
        fetchPolicy: "cache-and-network",
      };
    },
    props: ({ getSnPEvaluationCyclesQuery }) => {
      const { networkStatus, variables } = getSnPEvaluationCyclesQuery;

      const data = getSnPEvaluationCyclesFromCache(variables);
      const newSnpCycles = getSnpV2Cycles({
        cycles: _.get(data, "snpEvaluationCycles", []),
      });

      return {
        isData: !_.isEmpty(data),
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          networkStatus
        ),
        cycles: newSnpCycles,
      };
    },
  }),
  withLoader
)(TagSnp);

TagSnp.defaultProps = {
  taggedPractices: [],
  showApprovedPractices: true,
  parentCycleId: "",
  parentSetId: "",
  itemGroupType: "",
  testId: "appComponents-tagSnpModal",
  steps: ["CYCLE_LIST", "STANDARD_LIST"],
  currentStepIndex: 0,
  setCurrentStepIndex: _.noop,
};

TagSnp.propTypes = {
  itemGroupType: PropTypes.string,
  showApprovedPractices: PropTypes.bool,
  testId: PropTypes.string,
  reportCourseFilters: PropTypes.object,
  assignmentFeedFilters: PropTypes.object,
  currentStepIndex: PropTypes.number,
  setCurrentStepIndex: PropTypes.func,
};
